import { useCallback, useMemo, useState, type FC } from 'react';
import {
  ActionableNotification,
  Button,
  IconButton,
  InlineNotification,
  OverflowMenu,
  OverflowMenuItem,
  RadioButton,
  TextInput,
  Toggle,
} from '@carbon/react';
import { Information, Renew, Reset } from '@carbon/react/icons';
import { DataTable } from 'components/data-table';
import type { DataTableColumnDef } from 'components/data-table/types';
import { FloatingTooltip } from 'components/floating-tooltip';
import { formatFixed } from 'utils/number';
import { formatFeet, landingMoveQuestion, wellborePointName } from './utils';
import type {
  WellborePoint,
  WellborePointsResponse,
} from 'types/api-responses';
import styles from './wellbore-points-panel.module.scss';

export interface WellborePointsPanelProps {
  /** The derived points read; null while it has not loaded or the backend has none. */
  points: WellborePointsResponse | null;
  loading: boolean;
  /** Whether any point carries a depth yet; picks which guidance banner shows. */
  hasPointDepths: boolean;
  /** Whether this workspace may be edited (a promoted one is read-only). */
  editable: boolean;
  /** The Target TVD field, as typed (digits with separators); '' when blank. */
  targetTvd: string;
  onTargetTvdChange: (value: string) => void;
  tvdInvalid: boolean;
  /** "Use Target Zone Selection": follows the confirmed zone's grid instead of the TVD. */
  useTargetZone: boolean;
  /** False until a zone is confirmed and it has a grid at the well. */
  targetZoneAvailable: boolean;
  onUseTargetZoneChange: (checked: boolean) => void;
  /** "Keep Survey Depths" (UAT 25): an untouched survey is kept, and the depth
   *  controls held, while this is on. Null (no switch) where there is no survey. */
  keepSurveyDepths?: boolean | null;
  onKeepSurveyDepthsChange?: (checked: boolean) => void;
  /** Update Wellbore Depths — derives every vertex's depth in one refresh. */
  onUpdateDepths: () => void;
  updateDisabled: boolean;
  /** Revert Geometry — back to the geometry shown before the last derivation. */
  onRevert: () => void;
  revertDisabled: boolean;
  onEditPoint: (point: WellborePoint) => void;
  onDeletePoint: (point: WellborePoint) => void;
  /** One landing point per wellbore: picking a row moves it there. */
  onSetLandingPoint: (point: WellborePoint) => void;
  /** Why the last edit, delete or landing-point change from this table failed. */
  writeError?: string | null;
  onDismissWriteError?: () => void;
}

interface PointRow extends Record<string, unknown> {
  id: string;
  point: WellborePoint;
  /** True on the surface hole and bottom hole: never the landing point, never deleted. */
  isEnd: boolean;
  /** The elected landing point, or the default one when none is elected yet. */
  isLanding: boolean;
}

// Two items plus the divider — enough to tell whether the menu fits below its trigger.
const ROW_MENU_HEIGHT_PX = 80;

const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZES = [25, 50, 100];

/**
 * A coordinate as the table prints it.
 * @param {number | null} value The value.
 * @param {number} decimals Decimal places, the same minimum and maximum.
 * @return {string} The printable value.
 */
const num = (value: number | null, decimals = 0): string =>
  formatFixed(value, decimals, '—');

/**
 * Which point reads as the landing point. An elected one wins; with none elected it is the
 * station the first step of 80 degrees or more starts from, where the lateral begins, which
 * the backend reads with the estimated lateral's own segment math (UAT item 10). No step reaching it
 * leaves the LP unassigned. Display only: nothing is written until the operator picks a row.
 * @param {WellborePointsResponse | null} response The points read.
 * @return {number | null} The landing point's index, or null.
 */
const landingIndexOf = (
  response: WellborePointsResponse | null
): number | null => {
  const points = response?.points ?? [];
  const elected = points.findIndex((point) => point.pointType === 'lp');
  if (elected !== -1) return elected;
  return response?.firstHorizontalIndex ?? null;
};

interface LandingMoveConfirmProps {
  question: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The inline confirm for a far Landing Point move (UAT item 10): said above the table
 * rather than in a browser dialog, and nothing is written until "Move it".
 * @param {LandingMoveConfirmProps} props The question and the two answers.
 * @return {JSX.Element} The banner.
 */
const LandingMoveConfirm: FC<LandingMoveConfirmProps> = ({
  question,
  onConfirm,
  onCancel,
}) => (
  // Actionable rather than inline: Carbon refuses buttons inside an inline one.
  <ActionableNotification
    className={styles.guidance}
    kind="warning"
    inline
    lowContrast
    hideCloseButton
    hasFocus={false}
    title={question}
  >
    <div className={styles.confirmActions}>
      <Button kind="primary" size="sm" onClick={onConfirm}>
        Move it
      </Button>
      <Button kind="secondary" size="sm" onClick={onCancel}>
        Keep current
      </Button>
    </div>
  </ActionableNotification>
);

/**
 * A row's point name. The default landing point reads as LP, as an elected one does,
 * unless the operator already typed it something else.
 * @param {PointRow} row The row.
 * @return {string} The label.
 */
const pointRowName = (row: PointRow): string => {
  if (row.isLanding && row.point.pointType === 'unassigned') return 'LP';
  return wellborePointName(row.point);
};

interface PointsGuidanceProps {
  hasDepths: boolean;
  pointCount: number;
  /** A landing point shows: elected, or auto-picked on a survey well (UAT item 10). */
  hasLanding: boolean;
}

/**
 * The operator's next step on the points, one at a time: depths first, then enough
 * points to land on, then the landing point. Each replaces the one before rather than
 * stacking, because only one is ever the live step. Once a landing point shows, elected
 * or auto-picked on a survey well, there is no next step left to ask for, so nothing
 * shows (review item 9; UAT item 10: "no banner unless a user changes it").
 * @param {PointsGuidanceProps} props Whether depths exist, how many points, and a shown LP.
 * @return {JSX.Element | null} The banner, or nothing.
 */
const PointsGuidance: FC<PointsGuidanceProps> = ({
  hasDepths,
  pointCount,
  hasLanding,
}) => {
  if (!hasDepths) {
    return (
      <InlineNotification
        className={styles.guidance}
        kind="warning"
        lowContrast
        hideCloseButton
        title="These Wellbore points have no depths yet."
        subtitle="Save edited depths for wellbore points, or select a Target Zone to derive depths for every point."
      />
    );
  }
  // A two-point stick has nothing between SHL and BHL to land on, so asking for the
  // landing point would ask for an answer the geometry cannot carry.
  if (pointCount < 3) {
    return (
      <InlineNotification
        className={styles.guidance}
        kind="warning"
        lowContrast
        hideCloseButton
        title="Only two wellbore points."
        subtitle="For more accurate representation add points to the map."
      />
    );
  }
  if (hasLanding) return null;
  return (
    <InlineNotification
      className={styles.guidance}
      kind="info"
      lowContrast
      hideCloseButton
      title="Set the Landing Point (LP)."
      subtitle="Use the Landing Point column to select or adjust the first point where the wellbore enters the target zone."
    />
  );
};

interface PointActionsMenuProps {
  point: WellborePoint;
  editable: boolean;
  onEditPoint: (point: WellborePoint) => void;
  onDeletePoint: (point: WellborePoint) => void;
}

/**
 * The row's actions. Carbon v11 has no viewport collision detection, so the room below
 * is measured on click capture and the menu flips up near the window's bottom. A
 * component so the direction state doesn't rebuild the memoised columns.
 * @param {PointActionsMenuProps} props The point and what may be done to it.
 * @return {JSX.Element} The actions menu.
 */
const PointActionsMenu: FC<PointActionsMenuProps> = ({
  point,
  editable,
  onEditPoint,
  onDeletePoint,
}) => {
  const [direction, setDirection] = useState<'top' | 'bottom'>('bottom');
  const label = `Actions for ${wellborePointName(point)}`;
  return (
    <span
      onClickCapture={(event) => {
        const { bottom } = event.currentTarget.getBoundingClientRect();
        setDirection(
          window.innerHeight - bottom < ROW_MENU_HEIGHT_PX ? 'top' : 'bottom'
        );
      }}
    >
      <OverflowMenu
        size="sm"
        flipped
        direction={direction}
        // Carbon portals the menu to the body at z-index 6000, but the panel
        // can sit inside the geometry tearsheet, a modal above it — without
        // this the menu opens underneath the sheet and the trigger looks dead.
        menuOptionsClass={styles.rowMenu}
        aria-label={label}
        // Carbon v11 names an icon-only menu from iconDescription, not aria-label.
        iconDescription={label}
      >
        <OverflowMenuItem
          // A point with both axes pinned has nothing left to edit; either one
          // open is still worth opening the editor for.
          itemText="Edit point"
          disabled={!editable || (point.locationLocked && point.depthLocked)}
          onClick={() => onEditPoint(point)}
        />
        {/* Shown disabled on the ends rather than hidden, so the rule reads as a
            rule instead of a missing control. */}
        <OverflowMenuItem
          itemText="Delete point"
          isDelete
          hasDivider
          disabled={!editable || !point.deletable}
          onClick={() => onDeletePoint(point)}
        />
      </OverflowMenu>
    </span>
  );
};

/**
 * Story 7's Wellbore Points panel: the depth controls and the points the geometry is
 * made of. Positions are edited on the map or in the point editor; depths come only
 * from Update Wellbore Depths. The ends are never deleted or made the landing point.
 * @param {WellborePointsPanelProps} props The points, the depth controls and the row actions.
 * @return {JSX.Element} The panel.
 */
export const WellborePointsPanel: FC<WellborePointsPanelProps> = ({
  points,
  loading,
  hasPointDepths,
  editable,
  targetTvd,
  onTargetTvdChange,
  tvdInvalid,
  useTargetZone,
  targetZoneAvailable,
  onUseTargetZoneChange,
  keepSurveyDepths = null,
  onKeepSurveyDepthsChange,
  onUpdateDepths,
  updateDisabled,
  onRevert,
  revertDisabled,
  onEditPoint,
  onDeletePoint,
  onSetLandingPoint,
  writeError = null,
  onDismissWriteError,
}) => {
  // The survey stays, so nothing that would replace its depths is offered.
  const surveyKept = keepSurveyDepths === true;
  const landingIndex = landingIndexOf(points);
  // A far move waits here for the operator's answer. Held by the row itself, so a
  // re-read of the points (new objects) drops a question that no longer applies.
  const [pendingMove, setPendingMove] = useState<{
    target: WellborePoint;
    question: string;
  } | null>(null);
  const pending =
    pendingMove && points?.points.includes(pendingMove.target)
      ? pendingMove
      : null;

  // Every election from the table comes through here; a far one is asked first.
  const electLandingPoint = useCallback(
    (target: WellborePoint) => {
      const list = points?.points ?? [];
      const question =
        landingIndex === null
          ? null
          : landingMoveQuestion(
              list,
              landingIndex,
              list.indexOf(target),
              points?.firstHorizontalIndex ?? null
            );
      if (question) {
        setPendingMove({ target, question });
        return;
      }
      setPendingMove(null);
      onSetLandingPoint(target);
    },
    [points, landingIndex, onSetLandingPoint]
  );

  const rows = useMemo<PointRow[]>(() => {
    const list = points?.points ?? [];
    return list.map((point, index) => ({
      id: String(point.positionalIndex),
      point,
      isEnd: index === 0 || index === list.length - 1,
      isLanding: index === landingIndex,
    }));
  }, [points, landingIndex]);

  const columns = useMemo<DataTableColumnDef<PointRow>[]>(
    () => [
      {
        // One landing point per wellbore, so the column is a radio group: picking a
        // row moves the landing point there. The ends never carry it.
        id: 'landingPoint',
        header: 'Landing Point (LP)',
        // Wide enough for the header to stay on one line, as the mock sizes it.
        size: 180,
        enableSorting: false,
        cell: (info) => {
          const { point, isEnd, isLanding } = info.row.original;
          if (isEnd) return null;
          return (
            <RadioButton
              name="wellbore-landing-point"
              id={`wellbore-landing-point-${point.positionalIndex}`}
              value={String(point.positionalIndex)}
              labelText={`Set ${wellborePointName(point)} as the landing point`}
              hideLabel
              checked={isLanding}
              disabled={!editable}
              onChange={() => electLandingPoint(point)}
              // The default LP shows checked before anything is elected, and clicking a
              // checked radio fires no change; a click on it elects it (bot thread).
              onClick={() => {
                if (isLanding && point.pointType !== 'lp')
                  electLandingPoint(point);
              }}
            />
          );
        },
      },
      {
        id: 'name',
        header: 'Point Name',
        size: 130,
        accessorFn: pointRowName,
      },
      {
        id: 'latitude',
        header: 'Latitude',
        size: 130,
        accessorFn: (row) => row.point.latitude,
        cell: (info) => num(info.row.original.point.latitude, 6),
      },
      {
        id: 'longitude',
        header: 'Longitude',
        size: 130,
        accessorFn: (row) => row.point.longitude,
        cell: (info) => num(info.row.original.point.longitude, 6),
      },
      {
        id: 'measuredDepthFt',
        header: 'Measured Depth',
        size: 140,
        accessorFn: (row) => row.point.measuredDepthFt,
        cell: (info) => formatFeet(info.row.original.point.measuredDepthFt),
      },
      {
        id: 'tvdFt',
        header: 'Depth (TVDKB)',
        size: 120,
        accessorFn: (row) => row.point.tvdFt,
        cell: (info) => formatFeet(info.row.original.point.tvdFt),
      },
      {
        id: 'tvdssFt',
        header: 'TVDSS',
        size: 120,
        accessorFn: (row) => row.point.tvdssFt,
        cell: (info) => formatFeet(info.row.original.point.tvdssFt),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 90,
        enableSorting: false,
        cell: (info) => {
          const { point } = info.row.original;
          return (
            <PointActionsMenu
              point={point}
              editable={editable}
              onEditPoint={onEditPoint}
              onDeletePoint={onDeletePoint}
            />
          );
        },
      },
    ],
    [editable, onEditPoint, onDeletePoint, electLandingPoint]
  );

  return (
    <section className={styles.section} aria-label="Wellbore Points">
      <div className={styles.header}>
        <h2 className={styles.title}>
          Wellbore Points
          <FloatingTooltip
            content="The points that define this wellbore's geometry. Add or move them on the map in Edit Wellbore Geometry mode."
            placement="top"
          >
            <Information className={styles.infoIcon} aria-hidden />
          </FloatingTooltip>
        </h2>
        <div className={styles.controls} aria-label="Geometry edit controls">
          {keepSurveyDepths !== null && (
            <Toggle
              id="wellbore-points-keep-survey-depths"
              size="sm"
              labelText="Keep Survey Depths"
              hideLabel
              labelA="Keep Survey Depths"
              labelB="Keep Survey Depths"
              toggled={keepSurveyDepths}
              disabled={!editable}
              onToggle={onKeepSurveyDepthsChange}
            />
          )}
          <TextInput
            id="wellbore-points-target-tvd"
            labelText="Target TVD (default permit depth)"
            placeholder="Target TVD (ft)"
            inputMode="numeric"
            size="sm"
            className={styles.tvdInput}
            value={targetTvd}
            disabled={!editable || surveyKept || useTargetZone}
            invalid={tvdInvalid}
            invalidText="Enter a valid depth in feet"
            onChange={(event) => onTargetTvdChange(event.target.value)}
          />
          {/* On = follow the confirmed zone's structure grid; off = land flat at the
              Target TVD beside it. The A/B labels carry the control's name so the
              switch reads without an Off/On caption. */}
          <Toggle
            id="wellbore-points-use-target-zone"
            size="sm"
            labelText="Use Target Zone Selection"
            hideLabel
            labelA="Use Target Zone Selection"
            labelB="Use Target Zone Selection"
            toggled={useTargetZone}
            // Switching ON needs a confirmed zone with a grid; switching OFF never does,
            // so a grid choice left from a cleared zone can still be turned off.
            disabled={
              !editable ||
              surveyKept ||
              (!targetZoneAvailable && !useTargetZone)
            }
            onToggle={onUseTargetZoneChange}
          />
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Renew}
            disabled={!editable || surveyKept || updateDisabled}
            onClick={onUpdateDepths}
          >
            Update Wellbore Depths
          </Button>
          <IconButton
            kind="ghost"
            size="sm"
            label="Revert Geometry"
            align="left"
            disabled={!editable || revertDisabled}
            onClick={onRevert}
          >
            <Reset size={16} />
          </IconButton>
        </div>
      </div>
      {/* Said here rather than at the top of the sheet, which is off screen while the
          operator works in this table. */}
      {editable && rows.length > 0 && (
        <PointsGuidance
          hasDepths={hasPointDepths}
          pointCount={rows.length}
          hasLanding={landingIndex !== null}
        />
      )}
      {editable && pending && (
        <LandingMoveConfirm
          question={pending.question}
          onConfirm={() => {
            setPendingMove(null);
            onSetLandingPoint(pending.target);
          }}
          onCancel={() => setPendingMove(null)}
        />
      )}
      {writeError && (
        <InlineNotification
          className={styles.writeError}
          kind="error"
          lowContrast
          title="Failed to save the wellbore geometry"
          subtitle={writeError}
          onCloseButtonClick={onDismissWriteError}
        />
      )}
      <DataTable<PointRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        isLoading={loading && rows.length === 0}
        initialTableState={{ page: 1, pageSize: DEFAULT_PAGE_SIZE }}
        pageSizes={PAGE_SIZES}
        scroll={{ mode: 'paginated' }}
        // UAT 24: a hundred rows to a page, ten on screen; the rest scroll under a
        // pinned header while the pagination stays put.
        maxBodyHeight="30rem"
        searchPlaceholder="Search values"
        config={{
          sorting: false,
          globalSearch: true,
          columnFilters: false,
          columnVisibility: false,
          columnReordering: false,
          columnResizing: false,
          columnPinning: false,
          export: false,
        }}
      />
    </section>
  );
};
